import { listen } from "@tauri-apps/api/event";
import { isTauri } from "@/lib/is-tauri";
import { getWindowOrigin } from "./use-window-origin";
import { captureDesktopChatAuthScopeGuard } from "./desktop-chat-auth-scope";
import {
  getFloatingFrameDisplayArea,
  rememberFloatingFrameDisplay,
} from "./floating-frame-displays";
import { clampFrameHeaderToSurface } from "@/lib/frame-window-gestures";
import {
  putSettingsFrame,
  useSettingsFrameStore,
  type SettingsFrame,
} from "./settings-frame-store";
import { settingsPocketMaxHeight } from "./tray-panel-geometry";
import { safeTauriUnlisten } from "./tauri-listener-cleanup";
import type { FrameDragCursor } from "./floating-frame-drag";

import type { SettingsFramePreview } from "./settings-frame-preview";

let drag: {
  sessionId: string;
  startX: number;
  startY: number;
  preview:
    | SettingsFramePreview
    | (() => SettingsFramePreview | undefined)
    | undefined;
  started: boolean;
  offsetX: number;
  offsetY: number;
  current: () => boolean;
} | null = null;

// The panel's padding plus its header: a pocket dropped at the very bottom of
// a display keeps this much, so it can still be dragged back up.
const MIN_DROPPED_HEIGHT = 56;

/**
 * A dropped pocket may fill the display it lands on from its top down to the
 * edge margin, so a tall page scrolls instead of running off screen.
 */
function dropSettingsFrame(frame: SettingsFrame): void {
  const area = getFloatingFrameDisplayArea(frame.displayId);
  const box = area
    ? clampFrameHeaderToSurface(frame.box, area, MIN_DROPPED_HEIGHT)
    : frame.box;
  putSettingsFrame({
    ...frame,
    box: area
      ? {
          ...box,
          height: Math.max(
            MIN_DROPPED_HEIGHT,
            settingsPocketMaxHeight(box.y - area.y, area.height),
          ),
        }
      : box,
    dragging: false,
    preview: undefined,
    previewId: undefined,
  });
}

/**
 * `surfaceHeight` is the pocket's rendered height. A bottom-pinned pocket sits
 * that far above its box's bottom edge, so the grab is measured from there and
 * the drag carries it away top-pinned without a jump.
 */
export function beginSettingsFrameDrag(
  event: { clientX: number; clientY: number },
  surfaceHeight: number,
  preview?: SettingsFramePreview | (() => SettingsFramePreview | undefined),
): void {
  const frame = useSettingsFrameStore.getState().frame;
  if (!frame) return;
  const origin = getWindowOrigin();
  const top =
    frame.pinnedEdge === "bottom"
      ? frame.box.y + frame.box.height - surfaceHeight
      : frame.box.y;
  drag = {
    sessionId: frame.sessionId,
    startX: origin.x + event.clientX,
    startY: origin.y + event.clientY,
    preview,
    started: false,
    offsetX: origin.x + event.clientX - frame.box.x,
    offsetY: origin.y + event.clientY - top,
    current: captureDesktopChatAuthScopeGuard(),
  };
}

export function moveSettingsFrameDrag(cursor: FrameDragCursor): void {
  const currentFrame = useSettingsFrameStore.getState().frame;
  const frame = currentFrame ? { ...currentFrame } : null;
  if (!drag) return;
  if (!frame || frame.sessionId !== drag.sessionId || !drag.current()) {
    drag = null;
    return;
  }
  if (!drag.started) {
    if (Math.hypot(cursor.x - drag.startX, cursor.y - drag.startY) < 3) {
      if (cursor.primaryDown === false) drag = null;
      return;
    }
    if (cursor.primaryDown === false) {
      drag = null;
      return;
    }
    drag.started = true;
    frame.pinnedEdge = "top";
    const preview =
      typeof drag.preview === "function" ? drag.preview() : drag.preview;
    frame.preview = preview;
    frame.previewId = preview ? crypto.randomUUID() : undefined;
  }
  if (cursor.workArea)
    rememberFloatingFrameDisplay(cursor.displayId, cursor.workArea);
  const area = cursor.workArea ?? getFloatingFrameDisplayArea(cursor.displayId);
  const box = {
    ...frame.box,
    x: cursor.x - drag.offsetX,
    y: cursor.y - drag.offsetY,
  };
  const moved = {
    ...frame,
    displayId: cursor.displayId,
    box: area
      ? clampFrameHeaderToSurface(box, area, MIN_DROPPED_HEIGHT)
      : box,
  };
  if (cursor.primaryDown === false) {
    dropSettingsFrame(moved);
    drag = null;
  } else putSettingsFrame({ ...moved, dragging: true });
}

export function endSettingsFrameDrag(): void {
  const frame = useSettingsFrameStore.getState().frame;
  if (drag?.started && frame?.sessionId === drag.sessionId && drag.current())
    dropSettingsFrame(frame);
  drag = null;
}

export function attachSettingsFrameDrag(): () => void {
  if (!isTauri()) return () => {};
  let disposed = false;
  const unlisteners: Array<() => void> = [];
  for (const name of ["cursor-position", "active-display-changed"]) {
    void listen<FrameDragCursor>(name, ({ payload }) =>
      moveSettingsFrameDrag(payload),
    ).then((cleanup) => {
      if (disposed) safeTauriUnlisten(cleanup);
      else unlisteners.push(cleanup);
    });
  }
  window.addEventListener("pointerup", endSettingsFrameDrag);
  return () => {
    disposed = true;
    drag = null;
    window.removeEventListener("pointerup", endSettingsFrameDrag);
    unlisteners.forEach(safeTauriUnlisten);
  };
}
